import { useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { ShoppingCart, Plus, ListChecks } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useThemeConfig } from "@/components/ui/theme-config-provider";
import { getEnhancedPrimaryList } from "@/services/shopping/lists/primary-list-enhanced.service";
import { useNavigationFix } from '@/hooks/useNavigationFix';
import { useToast } from "@/hooks/use-toast";
import { useEnhancedErrorHandler } from "@/hooks/useEnhancedErrorHandler";
import { useAccessibilityEnhanced } from "@/hooks/useAccessibilityEnhanced";
import { EnhancedLoadingState } from "@/components/ui/EnhancedLoadingState";

const PrimaryListSection = () => {
  const { navigateToShopping, isNavigating } = useNavigationFix();
  const { theme } = useThemeConfig();
  const { toast } = useToast();

  // ✅ FASE 4: Integrazione gestione errori migliorata e accessibilità
  const { handleAsyncAction } = useEnhancedErrorHandler();
  const { announceToScreenReader } = useAccessibilityEnhanced();

  // ✅ FIX: Usa il servizio enhanced che supporta offline
  const { data: primaryList, isLoading: isLoadingPrimary, error } = useQuery({
    queryKey: ['enhanced-primary-list'],
    queryFn: getEnhancedPrimaryList,
    retry: 3,
    retryDelay: 1000
  });

  // ✅ FASE 4: Navigazione migliorata con gestione errori robusta
  const handleGoToShopping = useCallback(async () => {
    const result = await handleAsyncAction(
      navigateToShopping,
      'primaryListSection.navigation',
      {
        loadingMessage: 'Apertura lista della spesa...',
        maxRetries: 1,
        fallbackAction: () => {
          announceToScreenReader('Impossibile aprire la lista della spesa', 'assertive');
        }
      }
    );

    if (result !== null) {
      announceToScreenReader('Lista della spesa aperta', 'polite');
    }
  }, [handleAsyncAction, navigateToShopping, announceToScreenReader]);

  const handleGoToLists = useCallback(() => {
    announceToScreenReader('Navigazione alle liste', 'polite');
    window.location.href = '/lists';
  }, [announceToScreenReader]);

  // ✅ FASE 4: Loading state migliorato con accessibilità
  if (isLoadingPrimary) {
    return (
      <Card 
        className="primary-list-section mb-6" 
        style={{
          borderRadius: theme.borderRadius || '0.5rem',
          backgroundColor: theme.cardBackgroundColor || '#ffffff'
        }}
        role="region"
        aria-label="Lista principale della spesa"
      >
        <CardContent className="pt-6">
          <EnhancedLoadingState
            isLoading={true}
            loadingMessage="Caricamento lista principale..."
            skeletonCount={1}
            aria-label="Caricamento dati lista principale"
          />
        </CardContent>
      </Card>
    );
  }

  // ✅ FASE 4: Gestione errore migliorata
  if (error) {
    return (
      <Card 
        className="primary-list-section mb-6" 
        style={{
          borderRadius: theme.borderRadius || '0.5rem',
          backgroundColor: theme.cardBackgroundColor || '#ffffff'
        }}
        role="region"
        aria-label="Lista principale della spesa"
      >
        <CardContent className="pt-6">
          <EnhancedLoadingState
            hasError={true}
            errorMessage="Impossibile caricare la lista principale"
            onRetry={() => window.location.reload()}
            aria-label="Errore caricamento lista principale"
          />
        </CardContent>
      </Card>
    );
  }

  if (!primaryList) {
    return (
      <Card 
        className="primary-list-section mb-6" 
        style={{
          borderRadius: theme.borderRadius || '0.5rem',
          backgroundColor: theme.cardBackgroundColor || '#ffffff'
        }}
        role="region"
        aria-label="Lista principale della spesa"
      >
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ListChecks className="h-5 w-5" aria-hidden="true" />
            Lista della Spesa
          </CardTitle>
        </CardHeader>
        <CardContent>
          <EnhancedLoadingState
            emptyStateMessage="Non hai ancora una lista principale della spesa."
            emptyStateAction={{
              label: "Crea Lista",
              onClick: handleGoToLists
            }}
            aria-label="Nessuna lista principale configurata"
          />
        </CardContent>
      </Card>
    );
  }

  const totalItems = primaryList.items?.length || 0;
  const completedItems = primaryList.items?.filter(item => item.checked)?.length || 0;
  const pendingItems = totalItems - completedItems;
  const completionPercentage = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  return (
    <Card
      className="primary-list-section mb-6"
      style={{
        borderRadius: theme.borderRadius || '0.5rem',
        backgroundColor: theme.cardBackgroundColor || '#ffffff',
        color: theme.sectionTitleColor || '#ffffff'
      }}
      role="region"
      aria-label="Lista principale della spesa"
    >
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShoppingCart className="h-5 w-5" aria-hidden="true" />
          {primaryList.name}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* ✅ FASE 4: Statistiche con accessibilità migliorata */}
          <div 
            className="grid grid-cols-3 gap-4 text-center"
                style={{
                  borderRadius: theme.borderRadius,
                  backgroundColor: theme.backgroundColor,
                  color: theme.textColor,
                }}
            role="group"
            aria-label="Statistiche lista della spesa"
          >
            <div>
              <p
                className="text-2xl font-bold"
                style={{color: theme.primaryColor || '#1c4b21' }}
                aria-label={`${totalItems} articoli totali`}
              >
                {totalItems}
              </p>
              <p className="text-sm text-gray-500">Totali</p>
            </div>
            <div>
              <p
                className="text-2xl font-bold"
                style={{ color: theme.warningColor || '#f59e0b' }}
                aria-label={`${pendingItems} articoli da comprare`}
              >
                {pendingItems}
              </p>
              <p className="text-sm text-gray-500">Da comprare</p>
            </div>
            <div>
              <p
                className="text-2xl font-bold"
                style={{ color: theme.successColor || '#10b981' }}
                aria-label={`${completedItems} articoli completati`}
              >
                {completedItems}
              </p>
              <p className="text-sm text-gray-500">Acquistati</p>
            </div>
          </div>

          {/* ✅ FASE 4: Barra di progresso con accessibilità */}
          {totalItems > 0 && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Progresso</span>
                <span aria-label={`${completionPercentage}% completato`}>
                  {completionPercentage}%
                </span>
              </div>
              <div
                className="w-full rounded-full h-2"
                style={{
                  backgroundColor: theme.warningColor || "#bce4bd"
                }}
                role="progressbar"
                aria-valuenow={completionPercentage}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`Progresso spesa: ${completionPercentage}% completato`}
              >
                <div
                  className="h-2 rounded-full transition-all duration-300"
                  style={{
                    backgroundColor: theme.primaryColor,
                    width: `${completionPercentage}%`
                  }}
                />
              </div>
            </div>
          )}

          <Button
            onClick={handleGoToShopping}
            className="w-full"
            disabled={isNavigating}
            style={{
              backgroundColor: theme.primaryColor,
              borderRadius: theme.buttonBorderRadius || '0.375rem',
              color: theme.textPrimaryColor,
              fontWeight: 'bold',
            }}
            aria-label={`${isNavigating ? 'Apertura in corso' : 'Vai alla spesa'} - Lista ${primaryList.name}`}
          >
            <ShoppingCart className="h-4 w-4 mr-2" aria-hidden="true" />
            {isNavigating ? 'Apertura...' : 'Vai alla Spesa'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PrimaryListSection;
